import React from "react";
import { Box, Button, Modal } from "@mui/joy";
import { CloseRoundedIcon } from "../../styles/StyledMUI";
import DarkCard from "../../components/common/DarkCard";
import DarkTypography from "../../components/common/DarkTypography";
import IconButtonCTA from "../../components/common/IconButtonCTA";
import {
  colorTokens,
  ctaAccentHoverColor,
  ctaAccentTextColor,
  textTokens,
  withAlpha,
} from "../../styles/globalStyles";

type ConfirmationModalProviderProps = {
  children: React.ReactNode;
};

export type ModalContextType = {
  showConfirmation: (
    title: string,
    message?: string,
    element?: React.ReactElement,
  ) => Promise<boolean>;
};

export type UseModalShowReturnType = {
  show: boolean;
  setShow: (show: boolean) => void;
  onHide: () => void;
};

export const ConfirmationModalContext = React.createContext<ModalContextType>(null!);

function useModalShow(): UseModalShowReturnType {
  const [show, setShow] = React.useState(false);

  const onHide = React.useCallback(() => {
    setShow(false);
  }, []);

  return { show, setShow, onHide };
}

export function useConfirmationModal(): ModalContextType {
  const ctx = React.useContext(ConfirmationModalContext);
  if (!ctx) {
    throw new Error("useConfirmationModal must be used within a ConfirmationModalProvider");
  }
  return ctx;
}

export function ConfirmationModalProvider({ children }: ConfirmationModalProviderProps) {
  const { setShow, show, onHide } = useModalShow();
  const [content, setContent] = React.useState<{
    title: string;
    message?: string;
    element?: React.ReactElement;
  } | null>(null);
  const resolver = React.useRef<((value: boolean) => void) | undefined>(undefined);

  const handleShow = React.useCallback(
    (title: string, message?: string, element?: React.ReactElement): Promise<boolean> => {
      setContent({ title, message, element });
      setShow(true);
      return new Promise((resolve) => {
        resolver.current = resolve;
      });
    },
    [setShow],
  );

  const handleYes = React.useCallback(() => {
    resolver.current?.(true);
    onHide();
  }, [onHide]);

  const handleCancel = React.useCallback(() => {
    resolver.current?.(false);
    onHide();
  }, [onHide]);

  const modalContext: ModalContextType = React.useMemo(
    () => ({ showConfirmation: handleShow }),
    [handleShow],
  );

  return (
    <ConfirmationModalContext.Provider value={modalContext}>
      {children}
      <Modal
        open={show}
        onClose={onHide}
        sx={{
          zIndex: 210000,
          "& .MuiModal-backdrop": {
            zIndex: -1,
            backdropFilter: "none",
            WebkitBackdropFilter: "none",
            backgroundColor: withAlpha(colorTokens.bg.overlay, 0.55),
          },
        }}
      >
        <Box
          sx={{
            position: "relative",
            zIndex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "100%",
            p: { xs: 1.5, sm: 2 },
            boxSizing: "border-box",
          }}
        >
          <DarkCard
            sx={{
              width: "100%",
              maxWidth: { xs: "min(95vw, 500px)", sm: 500 },
              p: 3,
              boxShadow: `0 8px 32px ${withAlpha(colorTokens.bg.overlay, 0.45)}`,
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
                gap: 1,
              }}
            >
              <DarkTypography component="h2" level="title-md" colorVariant="title">
                {content?.title}
              </DarkTypography>
              <IconButtonCTA aria-label="Fermer" ctaVariant="neutral" size="sm" onClick={onHide}>
                <CloseRoundedIcon />
              </IconButtonCTA>
            </Box>
            <DarkTypography mb={2} colorVariant="primary" sx={{ whiteSpace: "pre-line" }}>
              {content?.message}
            </DarkTypography>
            {content?.element}
            <Box
              sx={{ display: "flex", justifyContent: "flex-end", gap: 2, flexWrap: "wrap", mt: 3 }}
            >
              <Button
                variant="solid"
                onClick={handleYes}
                sx={{
                  backgroundColor: colorTokens.text.title,
                  color: ctaAccentTextColor,
                  "&:hover": {
                    backgroundColor: ctaAccentHoverColor,
                    color: ctaAccentTextColor,
                  },
                }}
              >
                Continuer
              </Button>
              <Button
                variant="soft"
                onClick={handleCancel}
                sx={{
                  backgroundColor: colorTokens.bg.surfaceMuted,
                  color: textTokens.color.primary,
                  border: `1px solid ${colorTokens.border.default}`,
                  "&:hover": {
                    backgroundColor: colorTokens.bg.hover,
                  },
                }}
              >
                Annuler
              </Button>
            </Box>
          </DarkCard>
        </Box>
      </Modal>
    </ConfirmationModalContext.Provider>
  );
}
